// ShortMessage.js
'use strict';
import { connect } from 'react-redux';
import { goBack } from 'redux-first-history';

import {
  SHORTS_CTA_DELAY_SECONDS,
  LIVESTREAM_SHORTS_CTA_DELAY_SECONDS,
} from '../RemoteConfigKeys.js';

import ShortMessage from '../component/ShortMessage.jsx';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getUserData from '../selector/getUserData.js';
import getOperationData from '../selector/getOperationData.js';
import getMessageData from '../selector/getMessageData.js';
import getMeData from '../selector/getMeData.js';
import getMessageDataWithVariable, {
  VariableName,
} from '../selector/getMessageDataWithVariable.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getUsername from '../selector/getUsername.js';
import getIsNewlyUnlockedMessage from '../selector/getIsNewlyUnlockedMessage.js';
import getAssetData from '../selector/getAssetData.js';
import getShareTemplates from '../selector/getShareTemplates.js';

import fetchMessageRating from '../action/fetchMessageRating.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import fetchMessageSegments from '../action/fetchMessageSegments.js';
import fetchUser from '../action/fetchUser.js';
import toggleModal from '../action/toggleModal.js';
import setPlayerData from '../action/setPlayerData.js';
import submitMessageRating from '../action/submitMessageRating.js';
import sendChatStart from '../action/sendChatStart.js';
import login from '../action/login.js';
import setFirstShortMessagePlayed from '../action/setFirstShortMessagePlayed.js';
import sendUnlockMessage from '../action/sendUnlockMessage.js';
import setGiftPanel from '../action/setGiftPanel.js';
import updateEngagementData from '../action/updateEngagementData.js';

import { getKeySystem } from '../resource/getDrmInfo.js';
import { COM_APPLE_FPS } from '../resource/drmConstants.js';
import i18n, { localeConvertor } from '../resource/i18n.js';
import { ShareType } from '../hook/useShortShareLink.js';

const isFairPlay = getKeySystem() === COM_APPLE_FPS;
const mapStateToProps = (state, { messageId }) => {
  const meUsername = getMeData(state, 'username');
  const senderId = getMessageData(state, messageId, 'senderId');
  const sourceLanguage = localeConvertor({
    locale: getMessageData(state, messageId, 'source_language'),
  });
  const currentLanguage =
    getOperationData(state, ['config'], 'language') || i18n.language;

  const expiresAtUnix = getMessageData(state, messageId, 'expiresAtUnix');
  const isStory = expiresAtUnix !== null;

  const assetIds = getMessageData(state, messageId, 'assetIds');
  const assetId = assetIds?.[0];
  const mediaType = assetId
    ? getAssetData(state, [assetId], 'contentType')
    : getMessageData(state, messageId, 'mediaType');

  return {
    meId: getMeData(state, 'id'),
    senderId,
    senderUsername: getUserData(state, senderId, 'username'),
    displayedSenderUsername: getUsername(state, senderId, 'displayNameFirst'),
    caption: getMessageDataWithVariable(
      state,
      messageId,
      'captionText',
      `${VariableName.USERNAME}:${meUsername}`
    ),
    originalCaptionText: getMessageDataWithVariable(
      state,
      messageId,
      'originalCaptionText',
      `${VariableName.USERNAME}:${meUsername}`
    ),
    ctaUrl: getMessageData(state, messageId, 'ctaUrl'),
    ratedAs: getMessageData(state, messageId, 'ratedAs'),
    ctaDelaySeconds: getRemoteConfigData(state, SHORTS_CTA_DELAY_SECONDS),
    livestreamCtaDelaySeconds: getRemoteConfigData(
      state,
      LIVESTREAM_SHORTS_CTA_DELAY_SECONDS
    ),
    unlockPrice: getMessageData(state, messageId, 'unlockPrice'),
    assetIds: getMessageData(state, messageId, 'assetIds'),
    badges: getMessageData(state, messageId, 'badges'),
    isFairPlay,
    isMuted: getOperationData(state, ['player'], 'isMuted'),
    isMessageRatedAsFetched: getNetworkingData(
      state,
      ['messages', messageId, 'rating'],
      'isFetched'
    ),
    hasFirstShortMessagePlayed: getOperationData(
      state,
      ['shorts'],
      'hasPlayed'
    ),
    shouldFetchMessageTranslation: true, // For withFetchTranslation
    isLiveStreaming: getLiveStreamData(state, [senderId], 'isLiveStreaming'),
    canViewMedia: getMessageData(state, messageId, 'canViewMedia'),
    isLanguageMatched: sourceLanguage === currentLanguage,
    timestampOffsetMilliseconds: getTimestampOffset(
      state,
      Accuracy.MILLISECOND
    ),
    isStory,
    viewScope: getMessageData(state, messageId, 'viewScope'),
    isFree: getMessageData(state, messageId, 'unlockPrice') === 0,
    isNewlyUnlocked: getIsNewlyUnlockedMessage(state, messageId),
    mediaType,
    shareButtonTemplates: getShareTemplates(state, ShareType.SHORT),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchMessageSegments: ({ messageId, manifestType }) =>
      dispatch(fetchMessageSegments({ messageId, manifestType })),
    fetchMessageDetail: ({ messageId }) =>
      dispatch(fetchMessageDetail({ id: messageId })),
    fetchUser: ({ id, httpProxyHeaders }) =>
      dispatch(fetchUser({ id, httpProxyHeaders })),
    fetchMessageRating: ({ id }) => dispatch(fetchMessageRating({ id })),
    toggleModal: payload => dispatch(toggleModal(payload)),
    setPlayerData: ({ isMuted }) =>
      dispatch(setPlayerData({ isMuted, isFirstUnmuted: false })),
    submitMessageRating: ({ id, rating }) =>
      dispatch(submitMessageRating({ id, rating })),
    sendChatStart: ({ userId }) => dispatch(sendChatStart({ userId })),
    setGiftPanel: data => dispatch(setGiftPanel(data)),
    login: () => dispatch(login()),
    setFirstShortMessagePlayed: data =>
      dispatch(setFirstShortMessagePlayed(data)),
    sendUnlockMessage: data => dispatch(sendUnlockMessage(data)),
    goBack: () => dispatch(goBack()),
    updateEngagementData: data => dispatch(updateEngagementData(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShortMessage);
